import { createSelector } from 'reselect';

import { carrierDataSelectors } from 'wanderu-ducks-carriers';
import { createSelectorFactory } from 'wanderu-ducks-utils';

import SLICE_NAMESPACE from '../types';
import {
    buildEndpointData,
    buildFakeItinerary,
    calculateItinerary,
    getNumberOfMidnightsPassed,
    getRoutedTripFees,
    getRoutedTripPricing,
    getSingleTripFees,
    getSingleTripPricing,
    isRoutedTrip,
    tripDuration,
} from './helpers';
import NAMESPACE from './types';

const ROUTED_TRIP_DELIMITER = '|';

function selectData(state) {
    return state[SLICE_NAMESPACE][NAMESPACE];
}

function retrieveTripData(data, tripId) {
    return data.trips[tripId];
}

function retrieveTripItineraryData(appState, tripId) {
    return selectData(appState).tripItineraries[tripId];
}

function retrieveTripInformationData(appState, tripId) {
    return selectData(appState).tripInformation[tripId];
}

function mapCarrierInfoData(carriers, tripObject) {
    if (!tripObject) {
        return undefined;
    }

    return carriers[tripObject.carrier] || {};
}

function mapDuration(tripObject) {
    if (!tripObject) {
        return undefined;
    }

    const {
        arrive_datetime_utc: arriveDateTime,
        depart_datetime_utc: departDateTime,
    } = tripObject;

    return {
        duration: tripDuration(departDateTime * 1000, arriveDateTime * 1000),
    };
}

function mapEndpointArriveData(appState, tripLegIds) {
    if (!tripLegIds || !tripLegIds.length) {
        return undefined;
    }

    // eslint-disable-next-line no-use-before-define
    const arrivalLeg = selectTripData(appState, tripLegIds[tripLegIds.length - 1]);

    if (!arrivalLeg || arrivalLeg.notFound) {
        return undefined;
    }

    // eslint-disable-next-line no-use-before-define
    const tripItinerary = selectTripItinerary(appState, tripLegIds[tripLegIds.length - 1]);
    let arriveEndpoint = buildEndpointData(arrivalLeg, 'arrive');

    /* it's possible for an operator to update the departure or arrival time of
     * a trip which we have already cached.  if a user asks for itinerary info
     * for such a trip, they're going to see a mismatch between the psearch
     * result time and the on-demand itinerary time unless we take care of that
     * mismatch here */
    if (tripItinerary) {
        const {
            itinerary,
            metadata,
        } = tripItinerary;

        if (metadata && metadata.complete && itinerary && itinerary.length) {
            const leg = itinerary[itinerary.length - 1];

            if (leg?.timestamp) {
                arriveEndpoint = {
                    ...arriveEndpoint,
                    localizedDateTime: leg.timestamp,
                };
            }
        }
    }

    return arriveEndpoint;
}

function mapEndpointDepartData(appState, tripLegIds) {
    if (!tripLegIds || !tripLegIds.length) {
        return undefined;
    }

    // eslint-disable-next-line no-use-before-define
    const departureLeg = selectTripData(appState, tripLegIds[0]);

    if (!departureLeg || departureLeg.notFound) {
        return undefined;
    }

    // eslint-disable-next-line no-use-before-define
    const tripItinerary = selectTripItinerary(appState, tripLegIds[0]);
    let departEndpoint = buildEndpointData(departureLeg, 'depart');

    /* see note in mapEndpointArriveData */
    if (tripItinerary) {
        const {
            itinerary,
            metadata,
        } = tripItinerary;

        if (metadata && metadata.complete && itinerary && itinerary.length) {
            const leg = itinerary[0];

            if (leg?.timestamp) {
                departEndpoint = {
                    ...departEndpoint,
                    localizedDateTime: leg.timestamp,
                };
            }
        }
    }

    return departEndpoint;
}

function getCarrierCodes(tripData) {
    if (!tripData || tripData.notFound) {
        return undefined;
    }

    if (isRoutedTrip(tripData)) {
        return [...tripData.tripLegs
            .reduce((carrierCodes, leg) => {
                if (!leg.trip_id) {
                    return carrierCodes;
                }

                return carrierCodes.add(leg.carrier);
            }, new Set())];
    }

    return [tripData.carrier];
}

/**
 * @param {Object} itineraryObject - raw trip itin info
 * @param {Object} tripData - raw trip data
 * @param {Object} carrierCollection - mapped data for all carriers
 * @return {Array<Object>} an array of itineraries (length 1 for regular trips, or 2+ for routed trips)
 */
function mapItinerary(itineraryObject, tripData, carrierCollection) {
    if (!tripData || tripData.notFound) {
        return undefined;
    }

    if (isRoutedTrip(tripData)) {
        return tripData.triplegs
            .filter((leg) => !!leg.trip_id)
            .reduce((itins, leg) => [
                ...itins,
                ...mapItinerary(leg.itinerary_info, leg, carrierCollection),
            ], []);
    }

    /* we can't always prioritize dynamically-fetched itin info over what's
     * stored inside the psearch result.  some trips on some carriers will cause
     * an empty array to be returned by wapi's itineraryinfo.json endpoint (e.g.
     * the only itin info fetched from the carrier's api gets filtered out by
     * wapi for one reason or another before satisfying our request) and in that
     * case we need to fall back to the data in the psearch result... */
    let source = itineraryObject;

    if (!source || !source.itinerary || !source.itinerary.length) {
        source = tripData.itinerary_info
            || buildFakeItinerary(tripData, carrierCollection[tripData.carrier]);
    }

    if (!source) {
        return undefined;
    }

    /* ...but we DO need to prioritize the dynamic metadata over the psearch
     * metadata, to indicate that we've looked for more data but didn't really
     * get any */
    if (itineraryObject) {
        source.metadata = itineraryObject.metadata;
    }

    return {
        itinerary: calculateItinerary(source.itinerary, tripData),
        metadata: {
            ...source.metadata,
            tripId: tripData.trip_id,
        },
    };
}

/* unmapped data */
function selectTripDataFactory(tripId) {
    return createSelector(
        selectData,
        (data) => retrieveTripData(data, tripId),
    );
}
const selectTripData = createSelectorFactory(selectTripDataFactory);

function selectTripItinerary(appState, tripId) {
    const tripData = selectTripData(appState, tripId);
    const itinData = retrieveTripItineraryData(appState, tripId);
    const carrierCodes = getCarrierCodes(tripData);
    const carrierData = carrierDataSelectors.selectCarrierData(appState, carrierCodes);

    return mapItinerary(itinData, tripData, carrierData);
}

function selectTripInformation(appState, tripId) {
    return retrieveTripInformationData(appState, tripId);
}

function selectLegIdsFactory(tripId) {
    return createSelector(
        (appState) => selectTripData(appState, tripId),
        (trip) => {
            if (!tripId) {
                return undefined;
            } if (!trip) {
                return tripId.split(ROUTED_TRIP_DELIMITER);
            }

            return trip.triplegs
                ? trip.triplegs.map((leg) => leg.trip_id).filter(Boolean)
                : [tripId];
        },
    );
}
const selectLegIds = createSelectorFactory(selectLegIdsFactory);

const selectTripArriveData = createSelectorFactory(selectTripArriveDataFactory);
function selectTripArriveDataFactory(tripId) {
    return createSelector(
        (state) => state, // TODO refactor - this negates any caching benefit
        (state) => selectLegIds(state, tripId),
        mapEndpointArriveData,
    );
}

const selectTripCarrierData = createSelectorFactory(selectTripCarrierDataFactory);
function selectTripCarrierDataFactory(tripId) {
    return createSelector(
        carrierDataSelectors.selectCarrierCollection,
        (state) => selectTripData(state, tripId),
        mapCarrierInfoData,
    );
}

const selectTripDepartData = createSelectorFactory(selectTripDepartDataFactory);
function selectTripDepartDataFactory(tripId) {
    return createSelector(
        (state) => state, // TODO refactor - this negates any caching benefit
        (state) => selectLegIds(state, tripId),
        mapEndpointDepartData,
    );
}

const selectTripDuration = createSelectorFactory(selectTripDurationFactory);
function selectTripDurationFactory(tripId) {
    return createSelector(
        (state) => selectTripData(state, tripId),
        mapDuration,
    );
}

const selectAllTripIds = createSelector(
    selectData,
    (data) => (data
        ? Object.keys(data.trips)
        : []),
);

const selectTripNumberOfMidnightsPassed = createSelectorFactory(
    selectTripNumberOfMidnightsPassedFactory,
);
function selectTripNumberOfMidnightsPassedFactory(tripId) {
    return createSelector(
        (state) => selectTripArriveData(state, tripId),
        (state) => selectTripDepartData(state, tripId),
        (arrive, depart) => {
            const {
                localizedDateTime: arriveDateTime,
            } = arrive;
            const {
                localizedDateTime: departDateTime,
            } = depart;

            return getNumberOfMidnightsPassed(departDateTime, arriveDateTime);
        },
    );
}

function selectTripDataPricing(appState, tripId) {
    const tripData = selectTripData(appState, tripId);
    if (!tripData) {
        return undefined;
    }

    const isRouted = isRoutedTrip(tripData);
    const pricingData = isRouted
        ? tripData.triplegs.map((leg) => leg.pricing)
        : tripData.pricing;

    const pricing = isRouted
        ? getRoutedTripPricing(pricingData)
        : getSingleTripPricing(pricingData);

    const fees = isRouted
        ? getRoutedTripFees(pricingData)
        : getSingleTripFees(pricingData);

    return { fees, pricing };
}

export {
    selectAllTripIds,
    selectLegIds,
    selectTripArriveData,
    selectTripCarrierData,
    selectTripData,
    selectTripDataPricing,
    selectTripDepartData,
    selectTripDuration,
    selectTripInformation,
    selectTripItinerary,
    selectTripNumberOfMidnightsPassed,
};
